/** Session flag: a stale-asset reload was already attempted in this tab. */
export const STALE_ASSET_RELOAD_SESSION_KEY =
  'th_dashboard_stale_asset_reload_v1'

const STALE_MESSAGE_SNIPPETS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
  'Unable to preload CSS for'
]

/**
 * Vite dispatches `vite:preloadError` with the error on `payload` (plain Event).
 * Some environments may use CustomEvent `detail`.
 *
 * @param {Event} event
 * @returns {unknown}
 */
export function getVitePreloadErrorFromEvent(event) {
  return event?.payload ?? event?.detail ?? null
}

/** Dedupes vite:preloadError + router.onError in the same turn before reload runs. */
let staleAssetRecoveryInFlight = false

function errorText(err) {
  if (!err) return ''
  const parts = [err.message, err.name, String(err), err.stack].filter(Boolean)
  return parts.join('\n')
}

/**
 * Fallback when `vite:preloadError` does not run (e.g. some lazy route failures).
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isStaleAssetLoadError(err) {
  if (!err || typeof err !== 'object') return false
  if ('name' in err && err.name === 'ChunkLoadError') return true
  const t = errorText(err)
  return STALE_MESSAGE_SNIPPETS.some((s) => t.includes(s))
}

/**
 * @typedef {'none'|'reload'|'reload-duplicate'|'report'} StaleAssetRecoveryOutcome
 */

/**
 * First matching failure in a tab reloads once (deploy overlap is common). After reload,
 * session key stays set; a second failure falls back to the error page.
 *
 * @param {unknown} err
 * @param {{ trustedStaleFailure?: boolean }} [options] — if true, err comes from Vite's `vite:preloadError` (skip string heuristics).
 * @returns {StaleAssetRecoveryOutcome}
 */
export function handleStaleAssetFailure(err, options = {}) {
  if (import.meta.env.DEV) return 'none'

  const trusted = options.trustedStaleFailure === true
  if (!trusted && !isStaleAssetLoadError(err)) return 'none'

  if (staleAssetRecoveryInFlight) {
    return 'reload-duplicate'
  }

  if (sessionStorage.getItem(STALE_ASSET_RELOAD_SESSION_KEY) === '1') {
    return 'report'
  }

  staleAssetRecoveryInFlight = true
  sessionStorage.setItem(STALE_ASSET_RELOAD_SESSION_KEY, '1')
  globalThis.location.reload()
  return 'reload'
}
